/** BAGHETA 🪄 — mobilarea și instalația, după practica obișnuită de locuință.
 *
 *  ==========================================================================
 *  CE FACE, ÎN DOUĂ PROPOZIȚII. Scrie în document mobilierul pe care regulile
 *  de așezare îl pun într-o primă schiță, și adaugă lângă el instalația
 *  electrică pe care mobilierul o cere. După aia totul e editabil: bagheta nu
 *  e un mod de lucru, e un PUNCT DE PORNIRE.
 *
 *  DE UNDE VINE AȘEZAREA. Din `design/mobilare.ts` — aceleași reguli ca la
 *  butonul «Mobilează automat» al unei camere: patul cu spatele la un perete
 *  fără ușă, frontul de bucătărie cu chiuveta sub fereastră și mașinile de
 *  spălat lângă ea, cada sau cabina de duș cu vasul și lavoarul lângă ele,
 *  nimic în baleiajul unei uși. Un modul amenajat cu bagheta arată deci exact
 *  ca unul mobilat cameră cu cameră — un singur așezător, două butoane.
 *  Felurile fără regulă acolo (WC-ul de serviciu, balconul, debaraua) iau
 *  așezarea catalogului machetei (`plan/arhMobilier3d::catalog`), ca să nu
 *  rămână goale.
 *
 *  Diferența dintre dedus și scris e toată: ce e dedus trăiește cât ține
 *  pagina și nu se poate atinge; ce e scris se mută, se rotește, se șterge și
 *  se salvează.
 *
 *  INSTALAȚIA e partea care ține de practică, nu de geometrie. Regulile de mai
 *  jos (`RETETE`) sunt cele pe care le respectă orice locuință care se predă:
 *  întrerupătorul pe partea clanței, la 1,05 m; două prize de o parte și de
 *  alta a patului, la noptiere; prizele de bucătărie peste blat, la 1,10;
 *  întrerupătorul băii AFARĂ din baie; nicio priză în baie la mai puțin de
 *  0,60 m de cadă. Fiecare are motivul ei scris lângă ea, fiindcă altfel peste
 *  un an nimeni nu mai știe care e normă și care e gust.
 *
 *  CE NU FACE. Nu dimensionează instalația (v. nota din `electric.ts`) și nu
 *  inventează camere: o cameră fără fel cunoscut primește lumină și un
 *  întrerupător, adică minimul care e adevărat despre orice încăpere, și atât.
 *  ==========================================================================
 */
import { mobilierCamerei } from "../../plan/arhMobilier3d";
import { FEL_DIN_MODEL, felulPiesei } from "../../plan/arhMobilierPlan";
import { MOBILABILE, mobileazaCamera } from "./mobilare";
import { aparatul } from "./electric";
import { t } from "../../../i18n";
import {
  asezareaLangaPiesa, cadrulPiesei, caPiesa, desfaSuprapunerile, fereste,
  inGol, peZid, peZidSpre,
  type Blocaj, type GolSimplu, type Parte, type PiesaAsezata, type ZidMaterial,
} from "./langaMobila";
import type { Gol3 } from "../../plan/arh3d";
import type {
  Camera, ElectricDoc, Gol, PiesaDoc, PlanTronson, Pt, TronsonDoc,
} from "../doc";

/** Camerele de circulație — prin ele se intră în celelalte. Aceeași listă ca
 *  în `doc::CIRCULATIE`, fiindcă e aceeași întrebare: ce e drum și ce e
 *  destinație. */
const CIRCULATIE = new Set(["hol", "hall", "sas"]);

/** Camerele UDE: acolo întrerupătorul stă afară, iar priza ține distanță de
 *  cadă. Nu e o preferință — e zonarea din normativ, unde zona 2 din jurul căzii
 *  și al dușului nu primește aparat de comutație. */
const UDE = new Set(["bath", "bath2", "shower", "wc"]);

const r3 = (v: number) => Math.round(v * 1000) / 1000;
const dist = (p: Pt, q: Pt) => Math.hypot(q[0] - p[0], q[1] - p[1]);

/** Punctul e în inel? Aceeași aruncare de rază ca peste tot. */
function inInel(p: Pt, ring: readonly Pt[]): boolean {
  let inauntru = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > p[1] !== yj > p[1]
        && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) inauntru = !inauntru;
  }
  return inauntru;
}

/** `peZid` și `peZidSpre` stau în `langaMobila.ts`, împreună cu regula care le
 *  cheamă: aceeași socoteală trebuie să iasă și când bagheta scrie instalația,
 *  și când o piesă e trasă pe pânză și aparatele ei o urmează. */

/** Ușile care dau în camera asta. Aceeași măsură ca în `arhMobilier3d`: un gol
 *  al cărui mijloc stă la mai puțin de 35 cm de conturul camerei e al ei. */
function usileCamerei(inel: readonly Pt[], goluri: readonly Gol[]): Gol[] {
  return goluri.filter((g) => {
    if (g.fel !== "usa") return false;
    const m: Pt = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
    let cea = Infinity;
    for (let i = 0; i < inel.length; i++) {
      const a = inel[i];
      const b = inel[(i + 1) % inel.length];
      const vx = b[0] - a[0];
      const vy = b[1] - a[1];
      const L2 = vx * vx + vy * vy;
      const t = L2 < 1e-12 ? 0
        : Math.max(0, Math.min(1, ((m[0] - a[0]) * vx + (m[1] - a[1]) * vy) / L2));
      cea = Math.min(cea, dist(m, [a[0] + vx * t, a[1] + vy * t]));
    }
    return cea < 0.35;
  });
}

/** UȘA DE INTRARE a camerei: cea dinspre o circulație, altfel prima.
 *
 *  Contează fiindcă întrerupătorul se pune lângă EA, nu lângă oricare ușă. Într-un
 *  living cu trei uși, două dau în dormitoare și una în hol; comutatorul aprins
 *  la intrare e cel de lângă hol. */
function usaDeIntrare(
  cam: Camera, camere: readonly Camera[], goluri: readonly Gol[],
): Gol | null {
  const usi = usileCamerei(cam.inel, goluri);
  if (!usi.length) return null;
  const spreCirculatie = usi.find((g) => {
    const m: Pt = [(g.a[0] + g.b[0]) / 2, (g.a[1] + g.b[1]) / 2];
    const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
    const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
    for (const s of [1, -1]) {
      const q: Pt = [m[0] - uy * 0.45 * s, m[1] + ux * 0.45 * s];
      if (inInel(q, cam.inel)) continue;         // partea asta e camera însăși
      const vecina = camere.find((c) => c !== cam && inInel(q, c.inel));
      if (vecina && CIRCULATIE.has(vecina.kind)) return true;
    }
    return false;
  });
  return spreCirculatie ?? usi[0];
}

/** TRECEREA unei camere FĂRĂ UȘĂ — deschiderea prin care se intră în ea.
 *
 *  ==========================================================================
 *  DE CE EXISTĂ. Întrerupătorul se pune lângă ușa de intrare, iar o cameră
 *  fără ușă nu primea niciunul: rămânea cu plafoniera aprinsă de nicăieri.
 *  MĂSURAT pe cele trei module transcrise, 4 din 40 de camere erau așa, și
 *  toate erau același lucru — BUCĂTĂRIA DESCHISĂ spre living. Ea chiar n-are
 *  ușă, deci lipsa nu e o greșeală de citire a planului; dar nici n-o scutește
 *  de întrerupător, fiindcă se intră în ea pe undeva.
 *
 *  CUM SE GĂSEȘTE. Pe unde camera se învecinează cu ALTĂ CAMERĂ fără zid între
 *  ele. Se merge pe contur din 10 în 10 cm și se pipăie o palmă în afară: dacă
 *  acolo e altă cameră, bucata aia de contur e trecere, nu perete. Bucățile
 *  vecine se leagă, iar cea mai lată e intrarea — dintr-o bucătărie deschisă
 *  pe două laturi se intră pe cea largă.
 *
 *  CÂT DE LATĂ TREBUIE SĂ FIE. Cel puțin `PRAG_TRECERE`. Sub atât nu e
 *  deschidere, e un colț în care două camere se ating — iar un întrerupător
 *  pus acolo ar fi pe muchia zidului, nu lângă o intrare.
 *
 *  Întoarce exact forma pe care o cere `langaUsa`, ca trecerea să se așeze cu
 *  aceeași regulă ca o ușă: o trecere ESTE ușa camerei, doar că fără tocuri.
 *  ========================================================================== */
const PAS_CONTUR = 0.1;
const PRAG_TRECERE = 0.8;
function trecereaCamerei(
  cam: Camera, camere: readonly Camera[],
): { a: Pt; b: Pt; c: Pt; latime: number } | null {
  const inel = cam.inel;
  /** Punctele de pe contur, din 10 în 10 cm, cu un semn: e trecere acolo? */
  const sir: { p: Pt; trece: boolean }[] = [];
  for (let i = 0; i < inel.length; i++) {
    const a = inel[i], b = inel[(i + 1) % inel.length];
    const L = dist(a, b);
    if (L < 1e-9) continue;
    const ux = (b[0] - a[0]) / L, uy = (b[1] - a[1]) / L;
    for (let d = 0; d < L; d += PAS_CONTUR) {
      const p: Pt = [a[0] + ux * d, a[1] + uy * d];
      // O palmă în afară, pe normala care IESE din cameră.
      let afara: Pt = [p[0] - uy * 0.25, p[1] + ux * 0.25];
      if (inInel(afara, inel)) afara = [p[0] + uy * 0.25, p[1] - ux * 0.25];
      sir.push({ p, trece: camere.some((c) => c !== cam && inInel(afara, c.inel)) });
    }
  }
  if (!sir.length) return null;
  // Cea mai lungă bucată de trecere, pe cercul conturului — o deschidere
  // poate sta călare pe primul punct, iar tăiată acolo ar ieși două jumătăți.
  let bun: { de: number; pana: number } | null = null;
  const n = sir.length;
  let i0 = 0;
  while (i0 < n && sir[i0].trece) i0++;
  if (i0 === n) return null;              // toată camera e trecere: n-are pereți
  for (let k = 0; k < n; k++) {
    const i = (i0 + k) % n;
    if (!sir[i].trece) continue;
    let j = k;
    while (j < n && sir[(i0 + j) % n].trece) j++;
    if (!bun || j - k > bun.pana - bun.de) bun = { de: k, pana: j };
    k = j;
  }
  if (!bun) return null;
  const a = sir[(i0 + bun.de) % n].p;
  const b = sir[(i0 + bun.pana - 1) % n].p;
  const latime = dist(a, b);
  if (latime < PRAG_TRECERE) return null;
  return { a, b, c: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], latime };
}

/** LÂNGĂ UȘĂ, PE PARTEA CLANȚEI, la 15 cm de toc.
 *
 *  `inauntru` alege de care parte a zidului: în cameră pentru o cameră uscată,
 *  afară pentru o baie — v. `UDE`. Se încearcă amândouă capetele golului și se
 *  ține cel care cade acolo unde trebuie; dacă niciunul nu cade bine (ușa e
 *  chiar în colț), se ia primul, fiindcă un întrerupător pus strâmb e mai bun
 *  decât unul lipsă.
 *
 *  15 cm e distanța obișnuită: cât să încapă tocul și rama aparatului, și cât
 *  să nimerești clapeta cu cotul, pe întuneric, intrând cu mâinile pline. */
const LANGA_TOC = 0.15;
function langaUsa(g: Gol, inel: readonly Pt[], inauntru: boolean): {
  q: Pt; deg: number;
} {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  const d = g.latime / 2 + LANGA_TOC;
  let primul: { q: Pt; deg: number } | null = null;
  for (const s of [1, -1]) {
    const pe: Pt = [g.c[0] + ux * d * s, g.c[1] + uy * d * s];
    const gasit = peZid(pe, inel);
    // Punctul de probă: o palmă în interiorul camerei, de la aparat.
    const rad = (gasit.deg * Math.PI) / 180;
    const probe: Pt = [gasit.q[0] + Math.cos(rad) * 0.25,
                       gasit.q[1] + Math.sin(rad) * 0.25];
    primul = primul ?? gasit;
    if (inInel(probe, inel) === inauntru && dist(gasit.q, pe) < 0.5) return gasit;
  }
  return primul!;
}

// ------------------------------------------------------------------ rețetele ---

/** Un aparat de pus: unde, ce fel, și de ce.
 *
 *  `pentru` e PIESA care-l justifică, ca referință la obiectul din listă —
 *  poziția lui în document se află abia la scriere, când lista e gata. Ea, cu
 *  `parte`, e tot ce trebuie ca aparatul să poată fi RE-AȘEZAT mai târziu, la
 *  fiecare milimetru cu care piesa e trasă pe pânză. */
type DePus = {
  kind: string; q: Pt; deg: number; h?: number;
  pentru?: PiesaDoc; parte?: Parte;
};

/** Cât de departe de cadă sau de duș are voie să stea o priză.
 *
 *  0,60 m. E marginea zonei 2 din jurul căzii, în care nu se montează aparate
 *  de comutație și prize obișnuite. Sub cifra asta, priza NU se pune — nu se
 *  mută mai încolo cu forța, fiindcă într-o baie de 3,9 m² «mai încolo» poate
 *  să nu existe, iar o baie fără priză e o baie corectă. */
const DEPARTE_DE_APA = 0.6;

/** Mijlocul dreptunghiului liber al camerei — unde cade plafoniera.
 *
 *  Nu centrul geometric: centrul unei camere în L cade în brațul care lipsește.
 *  Media vârfurilor e suficient de aproape pentru inelele noastre, iar plafoniera
 *  se poate oricum muta. */
const mijloc = (inel: readonly Pt[]): Pt => {
  const xs = inel.map((p) => p[0]);
  const ys = inel.map((p) => p[1]);
  const c: Pt = [(Math.min(...xs) + Math.max(...xs)) / 2,
                 (Math.min(...ys) + Math.max(...ys)) / 2];
  if (inInel(c, inel)) return c;
  // Cade în afară (cameră în L): se ia cel mai bun vârf al unei grile rare.
  let cel = c;
  let cea = -1;
  for (let i = 1; i < 8; i++) {
    for (let j = 1; j < 8; j++) {
      const p: Pt = [Math.min(...xs) + ((Math.max(...xs) - Math.min(...xs)) * i) / 8,
                     Math.min(...ys) + ((Math.max(...ys) - Math.min(...ys)) * j) / 8];
      if (!inInel(p, inel)) continue;
      const d = Math.min(...inel.map((v) => dist(p, v)));
      if (d > cea) { cea = d; cel = p; }
    }
  }
  return cel;
};

/** CE E O CAMERĂ pentru regulile de așezare: conturul ei, zidurile din jur și
 *  golurile lor. Ultimele două nu schimbă unde se PUNE un aparat, ci de unde se
 *  DĂ LA O PARTE: de pe beton și din uși. */
export type Imprejur = {
  inel: readonly Pt[];
  ziduri: readonly ZidMaterial[];
  goluri: readonly GolSimplu[];
  /** Ce stă în fața peretelui și n-are legătură cu mobila camerei: baleiajele
   *  ușilor. Mobilierul se adaugă la ele în rețetă, fiindcă acolo se știe care
   *  piesă e CHIAR motivul prizei și deci nu trebuie ocolită. */
  blocaje?: readonly Blocaj[];
};

/** BALEIAJUL UNEI UȘI, ca dreptunghi: tocurile ei și adâncimea foii spre
 *  interiorul camerei. Nu sfertul de cerc exact — o priză nu se pune nici sub
 *  coarda lui, iar dreptunghiul e ce folosește și mobilarea
 *  (`mobilare::Cadru`), deci cele două se feresc de același lucru. */
function baleiajul(g: Gol, inel: readonly Pt[]): Blocaj {
  const ux = (g.b[0] - g.a[0]) / (g.latime || 1);
  const uy = (g.b[1] - g.a[1]) / (g.latime || 1);
  let n: Pt = [-uy, ux];
  if (!inInel([g.c[0] + n[0] * 0.3, g.c[1] + n[1] * 0.3], inel)) n = [uy, -ux];
  const d = g.latime;
  return [g.a, g.b,
          [g.b[0] + n[0] * d, g.b[1] + n[1] * d] as Pt,
          [g.a[0] + n[0] * d, g.a[1] + n[1] * d] as Pt];
}

/** AMPRENTA unei piese pe pardoseală: cele patru colțuri ale ei, rotite. */
function amprenta(f: PiesaDoc): Blocaj {
  const p = caPiesa(f);
  const { lateral, inapoi } = cadrulPiesei(p);
  const out: Pt[] = [];
  for (const sl of [-1, 1]) for (const si of [-1, 1]) {
    out.push([p.c[0] + lateral[0] * ((sl * p.w) / 2) + inapoi[0] * ((si * p.h) / 2),
              p.c[1] + lateral[1] * ((sl * p.w) / 2) + inapoi[1] * ((si * p.h) / 2)]);
  }
  // Colțurile ies în ordinea (−,−) (−,+) (+,−) (+,+); inelul le vrea în tur.
  return [out[0], out[1], out[3], out[2]];
}

/** PE CE PARTE A PIESEI RĂMÂNE LOC.
 *
 *  «O priză în stânga sau dreapta canapelei» — care din ele nu se poate scrie
 *  în regulă, fiindcă depinde de cameră: pe o parte e colțul, pe alta e ușa,
 *  pe a treia e priza televizorului pusă acum două rânduri. Deci se încearcă
 *  amândouă și se ține cea care rămâne mai departe de ce e deja acolo —
 *  aparatele puse până acum și colțurile camerei.
 *
 *  Colțurile intră în socoteală fiindcă o priză la 5 cm de colț e o priză pe
 *  care mobila o acoperă și rama ei nu intră: în colț nu se montează.
 *
 *  O PARTE PE CARE NU SE POATE NICI NU CONCUREAZĂ. `asezareaLangaPiesa` dă
 *  `null` când tot peretele din spatele piesei, pe partea aia, e ușă sau
 *  fereastră — și atunci răspunsul e cealaltă parte, nu un loc pus cu forța
 *  peste deschidere. `null` de la amândouă înseamnă că piesa nu poate avea o
 *  priză laterală deloc; ce se face atunci ține de aparat, nu de aici. */
function parteaLibera(
  piesa: PiesaAsezata, unde: Imprejur, deja: readonly Pt[],
): { parte: Parte; q: Pt; deg: number } | null {
  let cel: { parte: Parte; q: Pt; deg: number } | null = null;
  let cea = -Infinity;
  for (const parte of ["dreapta", "stanga"] as Parte[]) {
    const r = asezareaLangaPiesa(piesa, parte, unde.inel, unde.ziduri, unde.goluri);
    if (!r) continue;
    const laAltele = deja.length ? Math.min(...deja.map((q) => dist(q, r.q))) : 9;
    const laColt = Math.min(...unde.inel.map((v) => dist(v, r.q)));
    const scor = Math.min(laAltele, laColt);
    if (scor > cea) { cea = scor; cel = { parte, ...r }; }
  }
  return cel;
}

/** INSTALAȚIA UNEI CAMERE, după felul ei și după ce s-a mobilat în ea.
 *
 *  `piese` sunt piesele deja așezate, în lume: prizele se pun UNDE E MOBILA,
 *  fiindcă exact asta le justifică. O priză de noptieră fără noptieră e o gaură
 *  în perete.
 *
 *  FIECARE PRIZĂ DE MOBILĂ PLEACĂ DE AICI CU PIESA EI ÎN BUZUNAR (`pentru`,
 *  `parte`). Nu e o adnotare: e chiar ce face regula să poată fi rulată din nou,
 *  singură, pe o piesă mutată — v. `langaMobila`.
 */
function reteta(
  cam: Camera, piese: readonly PiesaDoc[], usa: Gol | null,
  vecinaUsii: readonly Pt[] | null, unde: Imprejur,
  trecere: { a: Pt; b: Pt; c: Pt; latime: number } | null = null,
): DePus[] {
  const out: DePus[] = [];
  const arie = Math.abs(cam.arie ?? 0);
  const ud = UDE.has(cam.kind);
  const laFel = (k: string) => piese.filter((p) => p.kind === k);
  /** Unde s-a pus deja ceva în camera asta — pentru alegerea părții libere. */
  const puse = () => out.map((a) => a.q);

  /** APARATUL CARE N-A GĂSIT LOC NU SE SCRIE.
   *
   *  Toate așezările de mai jos pot întoarce `null`, fiindcă toate trec prin
   *  interdicția de gol din `fereste`. Aici e singurul loc în care asta se
   *  traduce în document: se pune ce are unde, și atât. O cameră cu o priză mai
   *  puțin e o cameră în care se vede că mai trebuie una; o priză desenată pe
   *  fereastră e o gaură dată în glaf. */
  const pune = (a: DePus | null) => { if (a) out.push(a); };

  /** LÂNGĂ PIESĂ, pe o parte anume. */
  const langa = (
    f: PiesaDoc, parte: Parte, kind: string, h?: number,
  ): DePus | null => {
    const r = asezareaLangaPiesa(caPiesa(f), parte, unde.inel, unde.ziduri,
                                unde.goluri);
    return r && { kind, q: r.q, deg: r.deg, pentru: f, parte,
                  ...(h != null ? { h } : {}) };
  };
  /** LÂNGĂ PIESĂ, pe partea pe care rămâne loc. */
  const langaLiber = (f: PiesaDoc, kind: string, h?: number): DePus | null => {
    const r = parteaLibera(caPiesa(f), unde, puse());
    return r && { kind, q: r.q, deg: r.deg, pentru: f, parte: r.parte,
                  ...(h != null ? { h } : {}) };
  };

  /** NU SUB MOBILĂ ȘI NU SUB FOAIA UȘII — pentru prizele obișnuite.
   *
   *  ========================================================================
   *  O priză de uz general e singura care chiar trebuie să rămână la îndemână:
   *  în ea se bagă aspiratorul, încărcătorul, un calorifer electric. Ascunsă
   *  după dulap sau după ușa deschisă, nu mai e o priză, e o cotă pe planșă.
   *
   *  NU SE APLICĂ LA CELELALTE, și dinadins: priza televizorului stă CHIAR în
   *  spatele comodei, cea de blat deasupra corpurilor de bucătărie la 1,10 m
   *  (adică peste ele, nu sub), iar cele de la capul patului lângă tăblie.
   *  Pentru alea «sub mobilă» e răspunsul corect, nu o greșeală.
   *
   *  PIESA EI NU SE OCOLEȘTE. Priza e lângă dulap FIINDCĂ e dulapul acolo;
   *  ocolindu-l, ar pleca de lângă propriul ei motiv.
   *  ======================================================================== */
  const laIndemana = (a: DePus | null): DePus | null => {
    if (!a) return null;
    const blocaje: Blocaj[] = [
      ...(unde.blocaje ?? []),
      ...piese.filter((f) => f !== a.pentru).map(amprenta),
    ];
    const f = fereste(a.q, a.deg, unde.inel, unde.ziduri, unde.goluri,
                      undefined, undefined, blocaje);
    return f ? { ...a, q: f.q, deg: f.deg } : a;
  };

  /** PRIZA UNUI ELECTROCASNIC — scara de alternative, în ordinea practicii.
   *
   *  ========================================================================
   *  Un frigider sau o mașină de spălat nu-și poate lua priza de oriunde: în
   *  spatele lui, împins la perete, nu se ajunge cu mâna, iar ștecherul îndoit
   *  acolo ține aparatul la doi centimetri de zid. De-aia priza lui se caută
   *  în lateral — și de-aia are nevoie de o scară, nu de o regulă:
   *
   *    1. ÎN LATERAL, pe partea pe care rămâne loc (`parteaLibera`);
   *    2. ÎN CEALALTĂ PARTE, dacă pe prima e ușă sau fereastră — tot pasul 1
   *       îl face, fiindcă el încearcă amândouă și le ține doar pe cele care
   *       chiar se pot;
   *    3. MAI ÎNCOLO PE ACELAȘI PERETE, până la un metru — e `CAUTARE_DIN_GOL`
   *       din `fereste`, adică depărtarea pe care o face oricare din primele
   *       două când locul cerut cade în deschidere;
   *    4. ÎN SPATE. E locul pe care doctrina îl evită, dar un aparat fără priză
   *       nu se poate pune în funcțiune: mai bine o priză la care se ajunge
   *       greu decât niciuna. Se ajunge aici doar când în lateral, de amândouă
   *       părțile și pe un metru, nu e decât gol;
   *    5. DELOC. Nici în spate nu e perete — piesa stă în mijlocul camerei sau
   *       cu spatele la o fereastră pe toată lățimea ei. Atunci nu se pune
   *       nimic, și asta e răspunsul adevărat: locul se alege pe șantier, nu
   *       se ghicește pe planșă.
   *  ========================================================================
   */
  const langaElectrocasnic = (f: PiesaDoc, kind: string): DePus | null =>
    langaLiber(f, kind) ?? langa(f, "spate", kind);

  // ---- LUMINA: orice încăpere are un corp de iluminat pe tavan ------------- #
  //
  // Excepția nu e o cameră, e un balcon: acolo se pune aplică pe peretele
  // clădirii, fiindcă deasupra nu e placă, e cerul sau balconul de sus.
  if (cam.kind === "balcony") {
    const a = usa ? langaUsa(usa, cam.inel, true) : null;
    out.push({ kind: "aplica", q: a?.q ?? mijloc(cam.inel), deg: a?.deg ?? 0 });
  } else {
    out.push({ kind: "plafoniera", q: mijloc(cam.inel), deg: 0 });
  }

  // ---- COMANDA: lângă ușa de intrare --------------------------------------- #
  //
  // AFARĂ pentru camerele ude. Într-o baie, un întrerupător obișnuit e interzis
  // de zonare, iar practica de șantier e să stea pe holul de dinaintea ei — de
  // unde se și aprinde lumina înainte să intri.
  // PE UȘĂ DACĂ ARE, ALTFEL PE TRECERE. O bucătărie deschisă n-are ușă, dar
  // are o intrare — iar o cameră fără întrerupător e o cameră în care nu se
  // aprinde lumina. Trecerea se așază cu aceeași regulă ca ușa (v.
  // `trecereaCamerei`), și mereu PE DINĂUNTRU: n-are foaie, deci n-are nici
  // parte de dincolo pe care să iasă comanda, cum are o baie.
  const intrarea = usa ?? trecere;
  if (intrarea) {
    const inauntru = usa ? !ud && cam.kind !== "store" : true;
    const gazda = inauntru ? cam.inel : (vecinaUsii ?? cam.inel);
    const p = langaUsa(intrarea as Gol, gazda, inauntru);
    // ÎNTRERUPĂTORUL SE FEREȘTE ȘI EL DE BETON. E același șanț în perete ca al
    // unei prize, iar lângă ușă zidul e de multe ori chiar cel portant.
    //
    // DAR EL NU SE ȘTERGE CÂND FERIREA NU GĂSEȘTE NIMIC. Interdicția de gol e
    // făcută pentru prize, care se pun din cauza mobilei și se pot lipsi;
    // întrerupătorul se pune din cauza UȘII, la 15 cm de tocul ei, iar «prea
    // aproape de o deschidere» e chiar definiția locului lui. O cameră fără
    // priză e o cameră; una fără întrerupător e o cameră în care nu se aprinde
    // lumina. Deci aici `null` înseamnă «rămâi unde te-a pus ușa».
    const f = fereste(p.q, p.deg, gazda, unde.ziduri, unde.goluri) ?? p;
    // COMUTATOR, nu întrerupător, în camerele mari de dormit și pe circulații:
    // acolo lumina chiar se stinge din alt loc decât cel din care s-a aprins —
    // din pat, sau de la celălalt capăt al coridorului.
    const dublu = cam.kind === "living" && arie >= 20;
    out.push({
      kind: (cam.kind === "bedroom" || cam.kind === "dormitor") && arie >= 10
        ? "comutator"
        : CIRCULATIE.has(cam.kind) && arie >= 6 ? "comutator"
          : dublu ? "intrerupator_dublu" : "intrerupator",
      q: f.q, deg: f.deg,
    });
  }

  // ---- PRIZELE, pe fel ------------------------------------------------------ #
  if (cam.kind === "bedroom" || cam.kind === "dormitor") {
    // DE O PARTE ȘI DE ALTA A PATULUI. E perechea care se cere întotdeauna:
    // veioza pe una, telefonul pe cealaltă.
    //
    // DIN CADRUL PATULUI, nu din noptiere. Patul stă cu spatele — cu tăblia —
    // la perete (v. `mobilare.ts`), deci «stânga» și «dreapta» ale lui sunt
    // chiar cele două capete ale tăbliei, și se citesc din unghiul lui. Regula
    // ținea înainte de noptiere: un dormitor în care nu încăpea niciuna rămânea
    // fără nicio priză la pat, deși patul era acolo.
    const pat = piese.find((f) => f.kind.startsWith("pat"));
    if (pat) {
      // O TĂBLIE CU FEREASTRĂ ÎNTR-O PARTE RĂMÂNE CU O SINGURĂ PRIZĂ. Nu se
      // mută amândouă pe partea care se poate: două prize lipite la un capăt
      // al patului nu sunt perechea cerută, sunt o priză dublă desenată de
      // două ori. Cea care are unde se pune, cealaltă nu.
      pune(langa(pat, "stanga", "priza_dubla"));
      pune(langa(pat, "dreapta", "priza_dubla"));
      // Al doilea comutator, la capul patului: lumina se stinge din pat.
      if (arie >= 10) pune(langa(pat, "stanga", "comutator", 0.9));
    }
    // Peretele liber: aspirator, calorifer electric, ce mai vine.
    const d = laFel("dulap")[0];
    if (d) pune(laIndemana(langaLiber(d, "priza")));
    // PRIZA TELEVIZORULUI, PE PERETELE DIN FAȚA PATULUI.
    //
    // Televizorul dintr-un dormitor se uită din pat, deci stă pe peretele spre
    // care patul PRIVEȘTE. Se trage o rază din mijlocul patului, înainte — `+y`
    // al cadrului lui, adică opusul tăbliei — și se ia peretele pe care-l
    // lovește; nimic nu se declară, totul se citește din unghiul patului, deci
    // un pat întors cu mâna își duce și priza asta cu el.
    //
    // ERA O RAZĂ DIN MIJLOCUL CAMEREI, nouă metri în sus sau în jos după cum
    // cădea primul vârf al conturului — adică un perete ales de forma inelului,
    // nu de unde se uită omul. Pe un dormitor în care patul stă pe latura
    // scurtă, priza ieșea pe peretele de la capul patului sau pe cel lateral:
    // «the priza TV should always be opposite the bed or the couch»
    // (19.10.2026). Fără pat nu se mai pune deloc: peretele din fața patului
    // nu există dacă nu există patul.
    if (arie >= 12 && pat) {
      const fata = cadrulPiesei(caPiesa(pat)).inapoi;
      const p = peZidSpre(caPiesa(pat).c, [-fata[0], -fata[1]], cam.inel);
      const f = p && fereste(p.q, p.deg, cam.inel, unde.ziduri, unde.goluri);
      if (f) out.push({ kind: "priza_tv", q: f.q, deg: f.deg });
    }
  } else if (cam.kind === "living") {
    // ÎN SPATELE TELEVIZORULUI: o priză de curent și una de semnal. Ele sunt
    // motivul pentru care peretele ăla e „peretele televizorului".
    //
    // Amândouă cer același loc — spatele comodei — și tocmai de-aia se pun așa:
    // desfacerea de la sfârșit le lipește una de alta, exact cum se montează,
    // în loc să le pună pe o cotă ghicită de o parte și de alta.
    const tv = laFel("comoda_tv")[0] ?? laFel("tv")[0];
    const cn = laFel("canapea")[0] ?? laFel("canapea_colt")[0];
    if (tv) {
      pune(langa(tv, "spate", "priza_dubla"));
      pune(langa(tv, "spate", "priza_tv"));
    } else if (cn) {
      // FĂRĂ COMODĂ, DAR CU CANAPEA: priza de semnal pe peretele spre care
      // privește canapeaua. Televizorul se uită de pe ea, deci peretele din
      // fața ei e peretele televizorului chiar dacă nu s-a așezat încă mobila
      // care să-l poarte — aceeași regulă ca la pat (19.10.2026, «always be
      // opposite the bed or the couch»). Fără canapea nu se pune: nu se știe
      // de unde s-ar uita cineva.
      const fata = cadrulPiesei(caPiesa(cn)).inapoi;
      const p = peZidSpre(caPiesa(cn).c, [-fata[0], -fata[1]], cam.inel);
      const f = p && fereste(p.q, p.deg, cam.inel, unde.ziduri, unde.goluri);
      if (f) out.push({ kind: "priza_tv", q: f.q, deg: f.deg });
    }
    // LÂNGĂ CANAPEA, la un capăt al ei: lampadar, încărcătoare. Nu în spatele
    // mijlocului, unde ar rămâne sub spătar și n-ar mai intra ștecherul.
    if (cn) pune(langaLiber(cn, "priza_dubla"));
    // LA MASĂ: un living care are loc de masă are și nevoie de curent acolo.
    const masa = laFel("masa")[0] ?? laFel("masa_rotunda")[0];
    if (masa) pune(laIndemana(langaLiber(masa, "priza")));
  } else if (cam.kind === "kitchen") {
    // PESTE BLAT, la 1,10 m: exact deasupra suprafeței de lucru, unde stau
    // aparatele mici. Câte una la fiecare al doilea corp — adică la vreo 1,2 m,
    // cât ține un cablu de fierbător.
    const corpuri = piese.filter((p) => p.kind === "corp_bucatarie"
      || p.kind === "corp_sertare" || p.kind === "chiuveta");
    corpuri.forEach((c, i) => {
      if (i % 2) return;
      pune(langa(c, "spate", "priza_blat"));
    });
    // ELECTROCASNICELE: priza NU în spatele lor, ci puțin în lateral — și, când
    // nici acolo nu se poate, pe scara din `langaElectrocasnic`, până la «nu se
    // pune deloc». Motivul lateralului e că în spatele unui frigider împins la
    // perete nu se ajunge cu mâna, iar ștecherul îndoit acolo ține aparatul la
    // doi centimetri de zid.
    //
    // FORȚA, pe circuit propriu: plita, cuptorul și mașina de spălat trag
    // singure cât toată camera, deci nu împart siguranța cu fierbătorul.
    const aragaz = laFel("aragaz")[0];
    if (aragaz) pune(langaElectrocasnic(aragaz, "priza_forta"));
    const frig = laFel("frigider")[0];
    if (frig) pune(langaElectrocasnic(frig, "priza"));
    const rufe = laFel("masina_spalat")[0];
    if (rufe) pune(langaElectrocasnic(rufe, "priza_forta"));
    const vase = laFel("masina_vase")[0];
    if (vase) pune(langaElectrocasnic(vase, "priza_forta"));
  } else if (ud && cam.kind !== "wc") {
    // APLICĂ PESTE LAVOAR: plafoniera singură lasă fața în umbră, exact în
    // locul în care cineva se bărbierește.
    const lav = laFel("lavoar")[0] ?? laFel("chiuveta")[0];
    const apa = [...laFel("cada"), ...laFel("dus")].map(
      (p) => [p.x.a, p.y.a] as Pt);
    const a = lav ? langa(lav, "spate", "aplica") : null;
    if (a) {
      out.push(a);
      // PRIZA, doar dacă lavoarul e destul de departe de apă — v. `DEPARTE_DE_APA`.
      // Într-o baie mică nu se pune niciuna, și asta e răspunsul corect.
      if (!apa.length || Math.min(...apa.map((x) => dist(a.q, x))) >= DEPARTE_DE_APA) {
        pune(langa(lav!, "spate", "priza", 1.15));
      }
    }
    // O mașină de spălat pusă DE MÂNĂ în baie își primește tot priza de forță;
    // mobilarea automată n-o mai pune aici, ci în bucătărie.
    const masina = laFel("masina_spalat")[0];
    if (masina) pune(langaElectrocasnic(masina, "priza_forta"));
  } else if (cam.kind === "hall" || cam.kind === "hol" || cam.kind === "sas") {
    // O priză pe hol: aspiratorul se bagă în priză undeva, iar „undeva" e aici.
    //
    // ȘI TOCMAI AICI BALEIAJELE CONTEAZĂ CEL MAI MULT: un hol e aproape numai
    // uși, deci locul ales la întâmplare pe perete are toate șansele să cadă
    // sub o foaie deschisă. Blocajele sunt ale camerei (`unde.blocaje`), adică
    // chiar baleiajele ușilor ei; mobilier nu prea are.
    const m = mijloc(cam.inel);
    const p = peZid([m[0] + 0.9, m[1]], cam.inel);
    const f = fereste(p.q, p.deg, cam.inel, unde.ziduri, unde.goluri,
                      undefined, undefined,
                      [...(unde.blocaje ?? []), ...piese.map(amprenta)]);
    // Un hol strâmt e numai uși: atunci nu se pune nimic. Aspiratorul se bagă
    // în priza din camera de alături, ca în orice apartament.
    if (f) out.push({ kind: "priza", q: f.q, deg: f.deg });
  }
  return out;
}

/** INSTALAȚIA UNEI SINGURE CAMERE, gata de scris în document.
 *
 *  E pasul pe care îl face bagheta cameră cu cameră, scos afară fiindcă se
 *  cere și singur: butonul «Pune instalația» din panoul camerei alese o cheamă
 *  pentru camera aia și atât, ca «Mobilează camera» de lângă el. Un editor în
 *  care singura cale spre instalație e bagheta pe tot modulul e un editor în
 *  care o cameră schimbată cere reluarea celorlalte douăzeci.
 *
 *  `indexPiesa` spune unde stă fiecare piesă în `plan.furniture` — de acolo iese
 *  legătura `pentru`, iar cine cheamă știe lista întreagă; rețeta nu.
 */
export function instalatiaCamerei(
  plan: PlanTronson, cam: Camera, piese: readonly PiesaDoc[],
  indexPiesa: (f: PiesaDoc) => number,
): ElectricDoc[] {
  const ziduri: ZidMaterial[] = plan.pereti.map((w) => ({
    inel: w.inel, beton: w.material === "beton",
  }));
  const goluri: GolSimplu[] = plan.goluri.map((g) => ({ a: g.a, b: g.b }));

  const usa = usaDeIntrare(cam, plan.camere, plan.goluri);
  // Camera de dincolo de ușă — acolo iese întrerupătorul unei băi.
  let vecina: Pt[] | null = null;
  if (usa) {
    const ux = (usa.b[0] - usa.a[0]) / (usa.latime || 1);
    const uy = (usa.b[1] - usa.a[1]) / (usa.latime || 1);
    for (const s of [1, -1]) {
      const q: Pt = [usa.c[0] - uy * 0.45 * s, usa.c[1] + ux * 0.45 * s];
      if (inInel(q, cam.inel)) continue;
      const v = plan.camere.find((c) => c !== cam && inInel(q, c.inel));
      if (v) { vecina = v.inel; break; }
    }
  }
  const puse = reteta(cam, piese, usa, vecina,
                      { inel: cam.inel, ziduri, goluri,
                        blocaje: usileCamerei(cam.inel, plan.goluri)
                          .map((g) => baleiajul(g, cam.inel)) },
                      usa ? null : trecereaCamerei(cam, plan.camere));

  // DOUĂ APARATE NU STAU UNUL PESTE ALTUL. Regulile cer de mai multe ori
  // același loc — priza televizorului și cea de semnal cer amândouă spatele
  // comodei, două electrocasnice lipite cer același capăt de blat —, iar
  // răspunsul nu e o cotă ghicită în regulă, ci împinsul lor pe lungul zidului
  // până se ating. V. `desfaSuprapunerile`.
  const desfacute = desfaSuprapunerile(puse.map((a) => ({
    q: a.q, deg: a.deg, d: aparatul(a.kind)?.d ?? 0.16, mobil: true,
  })), goluri);

  return puse.map((a, k) => {
    const pentru = a.pentru ? indexPiesa(a.pentru) : -1;
    return {
      kind: a.kind,
      x: { a: r3(desfacute[k].q[0]) }, y: { a: r3(desfacute[k].q[1]) },
      rot_deg: r3(a.deg),
      ...(a.h != null ? { h_m: a.h } : {}),
      ...(pentru >= 0 && a.parte ? { pentru, parte: a.parte } : {}),
    };
  });
}

// -------------------------------------------------------------------- bagheta ---

export type Rezultat = {
  doc: TronsonDoc;
  /** Ce s-a pus, ca să se poată spune în interfață. */
  piese: number;
  aparate: number;
  /** Camerele pe care bagheta nu le-a mobilat, cu motivul. */
  sarite: { nume: string; de_ce: string }[];
};

/** MOBILAREA ȘI INSTALAȚIA, puse în document.
 *
 *  ÎNLOCUIEȘTE, nu adaugă: a doua apăsare pe baghetă dă același rezultat ca
 *  prima, nu două canapele suprapuse. Ce a pus omul de mână se pierde — în
 *  afară de doze, care rămân pe loc — de-aia butonul întreabă înainte, în
 *  interfață, când documentul are deja ceva.
 *
 *  `cuMobilier: false` PĂSTREAZĂ MOBILA EXISTENTĂ și pune doar instalația peste
 *  ea. Rețetele citesc atunci piesele DIN DOCUMENT, nu pe cele pe care le-ar fi
 *  așezat ea — altfel prizele ar ieși lângă un pat care nu e acolo, iar asta e
 *  mai rău decât lipsa lor: o priză desenată pe planșă se și execută.
 */
export function mobileazaAutomat(
  doc: TronsonDoc, plan: PlanTronson,
  { cuMobilier = true, cuElectrice = true } = {},
): Rezultat {
  const goluri3: Gol3[] = plan.goluri
    .filter((g) => g.fel === "usa")
    .map((g) => ({ a: g.a, b: g.b, parapet: 0 }));
  /** Toate deschiderile modulului — uși ȘI ferestre. Ele sunt interdicția, nu
   *  preferința: nimic nu se montează peste ele. V. `langaMobila::fereste`. */
  const goluri: GolSimplu[] = plan.goluri.map((g) => ({ a: g.a, b: g.b }));

  const piese: PiesaDoc[] = cuMobilier ? [] : [...(doc.plan?.furniture ?? [])];
  // INSTALAȚIA, LA FEL CA MOBILIERUL: ori se scrie de la zero, ori se ține cea
  // de acum. Cele două jumătăți ale baghetei se cer separat (04.10.2026:
  // «separate auto furniture and auto electric, so that they are generated
  // separately»), deci fiecare trebuie să poată trece pe lângă cealaltă fără
  // s-o șteargă.
  // Mounting boxes stay even when the installation is rewritten: they are
  // placed and dimensioned by hand, and no recipe here puts one back.
  const pastrate = cuElectrice
    ? (doc.plan?.electrical ?? []).filter((e) => e.kind === "doza").map((e) => ({ ...e }))
    : [];
  const electrice: ElectricDoc[] = cuElectrice
    ? [...pastrate] : [...(doc.plan?.electrical ?? [])];
  const sarite: { nume: string; de_ce: string }[] = [];

  // ---- TABLOURILE, câte unul de apartament -------------------------------- #
  //
  // În holul locuinței, lângă ușa de intrare — acolo stă. `hall` e holul
  // apartamentului; `hol` e coridorul comun, care nu e al nimănui și are tabloul
  // lui, de scară. Un modul fără niciun `hall` (o garsonieră transcrisă fără hol)
  // primește unul singur, în prima cameră de circulație găsită; fără NICIO
  // circulație, niciun tablou — și atunci firele nu se desenează, ceea ce e
  // adevărat: nu se știe de unde ar pleca.
  const holuri = cuElectrice ? plan.camere.filter((c) => c.kind === "hall") : [];
  const deTablou = !cuElectrice ? []
    : holuri.length ? holuri
      : plan.camere.filter((c) => CIRCULATIE.has(c.kind)).slice(0, 1);
  for (const c of deTablou) {
    const usa = usaDeIntrare(c, plan.camere, plan.goluri);
    const p = usa ? langaUsa(usa, c.inel, true) : peZid(mijloc(c.inel), c.inel);
    // O PALMĂ MAI ÎNCOLO DECÂT ÎNTRERUPĂTORUL, ca să nu se calce cu el — dar de
    // care parte a lui, asta o spune ușa. Tabloul e lat de aproape jumătate de
    // metru, iar mutat orbește într-o parte el cade taman peste deschiderea pe
    // lângă care tocmai a fost așezat întrerupătorul: măsurat pe M5, amândouă
    // tablourile modulului ajungeau așa în ușa de intrare a apartamentului.
    // Deci se încearcă amândouă părțile și se ține cea care rămâne pe perete;
    // dacă niciuna nu se poate, tabloul rămâne în prima — el NU se poate lipsi,
    // fiindcă din el pleacă toate traseele modulului (v. `design/electric`).
    let ales: Pt | null = null;
    let primul: Pt | null = null;
    for (const semn of [1, -1]) {
      const rad = ((p.deg + 90 * semn) * Math.PI) / 180;
      const q: Pt = [p.q[0] + Math.cos(rad) * 0.45, p.q[1] + Math.sin(rad) * 0.45];
      primul = primul ?? q;
      if (!inGol(q, goluri)) { ales = q; break; }
    }
    const q = ales ?? primul!;
    electrice.push({
      kind: "tablou",
      x: { a: r3(q[0]) }, y: { a: r3(q[1]) },
      rot_deg: r3(p.deg),
    });
  }

  // ---- CAMERĂ CU CAMERĂ ---------------------------------------------------- #
  plan.camere.forEach((cam) => {
    let aleMele: PiesaDoc[];
    if (cuMobilier) {
      const dinainte = piese.length;
      // MOBILIERUL: regulile de așezare, v. nota de sus. Catalogul machetei
      // rămâne doar pentru felurile fără regulă — nu și pentru o cameră cu
      // regulă în care n-a încăput nimic: acela e un răspuns adevărat.
      if (MOBILABILE.has(cam.kind)) piese.push(...mobileazaCamera(cam, plan.goluri));
      for (const x of MOBILABILE.has(cam.kind) ? [] : mobilierCamerei(cam.kind, cam.inel, goluri3)) {
        const kind = FEL_DIN_MODEL[x.m];
        if (!kind) continue;        // un model fără fel scris: nu se inventează
        // CE NU CADE ÎN CAMERĂ NU SE SCRIE.
        //
        // Așezarea lucrează pe DREPTUNGHIUL camerei — cadrul ei orientat, minus
        // baleiajul ușilor —, iar pentru o cameră în L dreptunghiul ăla iese pe
        // brațul care lipsește. Măsurat pe M5, dormitorul de 13 m² în L: a doua
        // noptieră cădea la (5,08; 9,74), iar camera se termină la x = 4,05 —
        // o noptieră în coridor.
        //
        // În machetă asta se vedea de mult și trecea drept aproximare, fiindcă
        // deducția trăia cât ținea pagina. Scrisă în document, devine o piesă
        // adevărată, care se salvează, se publică și intră în antemăsurătoare.
        // Deci bagheta nu scrie decât ce chiar e în cameră. Așezarea rămâne cum
        // e — repararea ei ține de `arhMobilier3d`, și e altă discuție.
        if (!inInel([x.p[0], x.p[1]], cam.inel)) continue;
        const fel = felulPiesei(kind);
        piese.push({
          kind,
          x: { a: r3(x.p[0]) }, y: { a: r3(x.p[1]) },
          w_m: r3(x.d[0]), h_m: r3(x.d[1]),
          rot_deg: r3(((x.r % 360) + 360) % 360),
          // Cota: cea a AȘEZĂRII dacă o dă (televizorul pe comodă), altfel cea
          // a felului (corpul suspendat, hota). În metri de la pardoseală.
          ...(x.p[2] || fel?.z ? { z_m: r3(x.p[2] || fel?.z || 0) } : {}),
        });
      }
      aleMele = piese.slice(dinainte);
      if (!aleMele.length && !CIRCULATIE.has(cam.kind) && cam.kind !== "balcony") {
        sarite.push({
          nume: cam.kind,
          de_ce: cam.arie != null && cam.arie < 4
            ? t("auto.sarit_mica", { arie: cam.arie.toFixed(1) })
            : t("auto.sarit_fara_retea"),
        });
      }
    } else {
      // Piesele DIN DOCUMENT care cad în camera asta. Una care stă călare pe
      // două camere ține de cea în care îi cade centrul: o piesă e undeva, nu
      // în două locuri.
      aleMele = piese.filter((f) => inInel([f.x.a, f.y.a], cam.inel));
    }

    // INSTALAȚIA, peste mobilierul camerei — numai când e cerută. Chemată
    // singură, mobilarea lasă instalația de acum neatinsă.
    if (cuElectrice) {
      electrice.push(...instalatiaCamerei(plan, cam, aleMele,
                                          (f) => piese.indexOf(f)));
    }
  });

  // DESFACEREA FINALĂ, pe tot modulul. Cea din `instalatiaCamerei` nu vede
  // decât aparatele camerei ei, iar tabloul e pus înaintea tuturor, în holul
  // apartamentului: pe M2 cădea la 5 cm de întrerupătorul de lângă aceeași ușă,
  // adică peste el pe planșă. Aici se văd toate deodată.
  const toate = !cuElectrice ? [] : desfaSuprapunerile(electrice.map((e) => ({
    q: [e.x.a, e.y.a] as Pt,
    deg: e.rot_deg ?? 0,
    d: aparatul(e.kind)?.d ?? 0.16,
    mobil: e.kind !== "doza",
  })), goluri);
  toate.forEach((x, i) => {
    electrice[i].x = { ...electrice[i].x, a: r3(x.q[0]) };
    electrice[i].y = { ...electrice[i].y, a: r3(x.q[1]) };
  });

  return {
    doc: { ...doc, plan: { ...doc.plan!, furniture: piese, electrical: electrice } },
    piese: cuMobilier ? piese.length : 0,
    aparate: cuElectrice ? electrice.length - pastrate.length : 0,
    sarite,
  };
}
